import {useEffect, useRef, type RefObject} from 'react';

/**
 * Mirrors the latest `value` into a ref, synced every render in the commit phase.
 *
 * Lets a stable callback read the current value without being recreated when the
 * value changes (and without listing it as a dependency). This is the single
 * audited home for the no-deps "sync ref" effect — call sites stay free of the
 * repeated `eslint-js/no-restricted-syntax` eslint-disable.
 *
 * Note: the sync runs in a passive effect (commit phase), matching the timing of
 * the hand-written idiom it replaces. It is therefore only a drop-in for refs
 * that mirror a plain render-time value — not for refs that read derived state
 * (e.g. `store.getState()`) inside the effect, whose commit-phase read can differ
 * from the render-time value.
 */
export function useLatestRef<T>(value: T): RefObject<T> {
  const ref = useRef(value);

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: no-deps useEffect syncs the ref to the latest value every render (commit phase)
  useEffect(function syncLatestValue() {
    ref.current = value;
  });

  return ref;
}
